import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import USBTable from '@usb-shield/react-table';
import { getCoreRowModel } from '@tanstack/react-table';
import './Table.css';

/**
 * USBTableCompat - Compatibility wrapper for legacy USBTable usage
 * This component provides backward compatibility for code that was using
 * the old USBTable component from @usb-inner-src
 * 
 * It accepts the legacy API (columnData, tableData, fieldFor, etc.)
 * and internally uses @usb-shield/react-table.
 */
const USBTableCompat = ({
  columnData = [],
  tableData = [],
  currentPageIndex = 1,
  handlePageChange,
  paginationOptions = [10, 25, 50],
  handleItemCountChange,
  paginationLabels = {},
  paginationPosition = 'bottom',
  addClasses = '',
  displayVariant = 'auto',
  useBorder = 'true',
  labelText = '',
  onRowClick,
}) => {
  const [currentPage, setCurrentPage] = useState(currentPageIndex);
  const [pageSize, setPageSize] = useState(paginationOptions[0] || 10);
  const [sortConfig, setSortConfig] = useState(null);

  // Calculate total pages
  const totalPages = Math.ceil(tableData.length / pageSize);

  // Check if ANY column has sorting enabled (default true unless explicitly false)
  const hasAnySortableColumn = useMemo(() => {
    return columnData.some((col) => col.isSortable !== false);
  }, [columnData]);

  // Transform legacy columnData format to Shield table columns (v3 API)
  // Sorting respects the isSortable flag from each column config
  const shieldColumns = useMemo(() => {
    return columnData.map((col, index) => ({
      id: col.fieldFor || `col-${index}`,
      header: col.headerName || col.columnName || col.title || '',
      accessorKey: col.fieldFor,
      // Respect per-column isSortable setting (defaults to true if not specified)
      enableSorting: col.isSortable !== false,
      dataType: col.dataType || 'string',
      width: col.width,
      // Handle custom render (if value is already a React element)
      cell: (info) => {
        const row = info.row?.original || info.row;
        const value = row[col.fieldFor];
        const content = React.isValidElement(value) ? value : (value ?? '-');

        if (onRowClick && !col.disableRowClick) {
          return (
            <div
              className="usb-table-row-clickable-cell"
              onClick={() => onRowClick(row)}
              title="Click to view more details"
            >
              {content}
            </div>
          );
        }

        return content;
      }
    }));
  }, [columnData]);

  // Sort data if sortConfig is set
  // Enhanced to handle React elements and extract sortable values
  const sortedData = useMemo(() => {
    if (!sortConfig || !sortConfig.key) {
      return tableData;
    }

    // Helper to extract sortable value from potentially React elements
    const getSortableValue = (val) => {
      if (val == null) return null;
      
      // If it's a React element, try to extract text content
      if (React.isValidElement(val)) {
        // Check for props.children or specific data attributes
        const props = val.props;
        
        // Look for data-sort-value prop (custom attribute for sorting)
        if (props?.['data-sort-value'] !== undefined) {
          return props['data-sort-value'];
        }
        
        // Try to get text from children
        if (typeof props?.children === 'string') {
          return props.children;
        }
        if (typeof props?.children === 'number') {
          return props.children;
        }
        
        // For complex children, try to extract first text
        if (Array.isArray(props?.children)) {
          const textChild = props.children.find(
            child => typeof child === 'string' || typeof child === 'number'
          );
          if (textChild !== undefined) return textChild;
        }
        
        // Return empty string for complex React elements
        return '';
      }
      
      return val;
    };
    
    return [...tableData].sort((a, b) => {
      const aVal = getSortableValue(a[sortConfig.key]);
      const bVal = getSortableValue(b[sortConfig.key]);
      
      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const compare = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
        return sortConfig.direction === 'desc' ? -compare : compare;
      }
      
      // Number comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal;
      }
      
      // Mixed type comparison
      if (aVal < bVal) return sortConfig.direction === 'desc' ? 1 : -1;
      if (aVal > bVal) return sortConfig.direction === 'desc' ? -1 : 1;
      return 0;
    });
  }, [tableData, sortConfig]);

  // Get paginated data (after sorting)
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Handle sort
  const handleSort = (columnKey, direction) => {
    setSortConfig({ key: columnKey, direction: direction || 'asc' });
    setCurrentPage(1);
  };

  // Handle page change
  const onPageChange = (newPage) => {
    setCurrentPage(newPage);
    if (handlePageChange) {
      handlePageChange(newPage);
    }
  };

  // Handle page size change
  const onPageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setCurrentPage(1);
    if (handleItemCountChange) {
      handleItemCountChange(newSize);
    }
  };

  // Render pagination controls
  const renderPagination = () => (
    <div className="table-pagination">
      <div className="pagination-info">
        <div className="page-size-selector">
          <label htmlFor="page-size">{paginationLabels.rowDropdown || 'Rows per page:'}</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={onPageSizeChange}
            className="page-size-select"
          >
            {paginationOptions.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <span className="page-info">
          {paginationLabels.unit || 'Page'} {currentPage} {paginationLabels.preposition || 'of'} {totalPages || 1}
        </span>
      </div>
      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={paginationLabels.previousButton || 'Previous page'}
        >
          «
        </button>
        <button
          type="button"
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label={paginationLabels.nextButton || 'Next page'}
        >
          »
        </button>
      </div>
    </div>
  );

  // Shield Table v3 options object with manual sorting
  const tableOptions = useMemo(() => {
    const options = {
      data: paginatedData,
      columns: shieldColumns,
      getCoreRowModel: getCoreRowModel(),
    };

    // Only add sorting config if any column is sortable
    if (hasAnySortableColumn) {
      options.manualSorting = true;
      options.state = {
        sorting: sortConfig ? [{ id: sortConfig.key, desc: sortConfig.direction === 'desc' }] : []
      };
      options.onSortingChange = (updaterOrValue) => {
        const newSorting = typeof updaterOrValue === 'function' 
          ? updaterOrValue(sortConfig ? [{ id: sortConfig.key, desc: sortConfig.direction === 'desc' }] : [])
          : updaterOrValue;
        
        if (newSorting.length > 0) {
          const sort = newSorting[0];
          setSortConfig({ key: sort.id, direction: sort.desc ? 'desc' : 'asc' });
        } else {
          setSortConfig(null);
        }
        setCurrentPage(1);
      };
    }

    return options;
  }, [paginatedData, shieldColumns, sortConfig, hasAnySortableColumn]);

  return (
    <div className={`usb-table-compat ${addClasses} ${onRowClick ? 'has-row-click' : ''}`.trim()} style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {(paginationPosition === 'top' || paginationPosition === 'both') && tableData.length > 0 && renderPagination()}
      
      {tableData.length === 0 ? (
        <div className="table-empty">No data available</div>
      ) : (
        <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          <USBTable
            id="usb-compat-table"
            options={tableOptions}
            content={{
              caption: labelText || '',
              ariaLabel: labelText || 'Data table'
            }}
            isSortable={hasAnySortableColumn}
            isZebraStriped={false}
          />
        </div>
      )}

      {(paginationPosition === 'bottom' || paginationPosition === 'both') && tableData.length > 0 && renderPagination()}
    </div>
  );
};

USBTableCompat.propTypes = {
  columnData: PropTypes.arrayOf(
    PropTypes.shape({
      headerName: PropTypes.string,
      fieldFor: PropTypes.string,
      isSortable: PropTypes.bool,
      wrapContent: PropTypes.bool,
      dataType: PropTypes.string,
      headerAlignment: PropTypes.string,
    })
  ),
  tableData: PropTypes.arrayOf(PropTypes.object),
  currentPageIndex: PropTypes.number,
  handlePageChange: PropTypes.func,
  paginationOptions: PropTypes.arrayOf(PropTypes.number),
  handleItemCountChange: PropTypes.func,
  paginationLabels: PropTypes.object,
  paginationPosition: PropTypes.string,
  addClasses: PropTypes.string,
  displayVariant: PropTypes.string,
  useBorder: PropTypes.string,
  labelText: PropTypes.string,
  onRowClick: PropTypes.func,
};

export default USBTableCompat;
